/**
 * `kanban` — monday's `KanbanBoardView` (`specs/M4-V2-kanban.md`).
 *
 * A lane is a group-by bucket: the settings here say **which column** is the lane axis and
 * **what a card shows**; everything else (the buckets, their order, their colours, the counts,
 * the paging) is B4 §8.8's grouped read, unchanged.
 */
import { z } from 'zod';
import { defineViewType, normaliseOverDefaults } from '../types.js';
import { mondayShapedSettings } from '../from-monday.js';
import { columnSelectionSchema, EMPTY_COLUMN_SELECTION } from '../column-selection.js';
// The kind's own monday reader, so `normaliseSettings` can recognise an imported blob
// (P-025). Re-exported below as well, which is how every caller outside has always had it.
import { kanbanSettingsFromMonday } from './from-monday.js';
export * from './from-monday.js';
export * from './to-monday.js';
/** The `settings_str` keys this kind models. Everything else is an extra. */
export const KANBAN_MONDAY_KEYS = new Set([
    'pivot_by_column',
    'assignee_column',
    'card_columns',
    'gallery_column',
    'display_mode',
    'divide_by_group',
    'hide_empty_groups',
]);
export const kanbanViewSettingsSchema = z.object({
    pivotColumnId: z.string().nullable(),
    assigneeColumnId: z.string().nullable(),
    cardColumns: columnSelectionSchema,
    coverColumns: columnSelectionSchema,
    coverDisplayMode: z.enum(['crop', 'fit']),
    divideByGroup: z.boolean(),
    hideEmptyGroups: z.boolean(),
    collapsedLaneKeys: z.array(z.string()),
    mondayKeys: z.array(z.string()),
    mondayExtras: z.record(z.string(), z.unknown()),
});
export const DEFAULT_KANBAN_VIEW_SETTINGS = {
    pivotColumnId: null,
    assigneeColumnId: null,
    cardColumns: EMPTY_COLUMN_SELECTION,
    coverColumns: EMPTY_COLUMN_SELECTION,
    coverDisplayMode: 'crop',
    divideByGroup: false,
    hideEmptyGroups: false,
    collapsedLaneKeys: [],
    mondayKeys: [],
    mondayExtras: {},
};
/** Measured on the sandbox: lane 260 wide on a 276 pitch, card 240 (`[V2§2.1]`). */
export const KANBAN_METRICS = {
    laneWidth: 260,
    lanePitch: 276,
    laneHeaderHeight: 40,
    cardWidth: 240,
    cardGap: 8,
    cardHeaderHeight: 38,
    cardCellHeight: 34,
    /** Between two cell rows; measured as a 42 px pitch on a 34 px row. */
    cardCellGap: 8,
    cardPadding: 8,
    coverHeight: 150,
};
/**
 * The height a card renders at, given how many columns it shows (`[V2§2.1]`): the 8 px gap
 * above the card, the 38 px header, and — when it shows any column — a block of
 * `8 + 42·n` for the rows at a 42 px pitch inside 8 px padding. Measured **96** for one
 * column and **138** for two.
 */
export function kanbanCardHeight(cellCount, hasCover = false) {
    const pitch = KANBAN_METRICS.cardCellHeight + KANBAN_METRICS.cardCellGap;
    const cells = cellCount > 0 ? pitch * cellCount + KANBAN_METRICS.cardPadding : 0;
    const cover = hasCover ? KANBAN_METRICS.coverHeight : 0;
    return KANBAN_METRICS.cardGap + KANBAN_METRICS.cardHeaderHeight + cells + cover;
}
/** How far the lane header's fill is lightened toward white — measured, `[V2§2.2]`. */
export const LANE_HEADER_LIGHTEN = 0.2;
/**
 * The lane header's fill: the bucket colour lightened 20 % toward white, measured on four of
 * the sandbox status column's five labels (`[V2§2.2]`, q401 covers the fifth):
 * `#fdab3d → rgb(253,188,100)`, `#00c875 → rgb(51,211,145)`, `#579bfc → rgb(121,175,253)`,
 * `#784bd1 → rgb(147,111,218)`.
 *
 * The mix is done here rather than with CSS `color-mix` because a browser serialises that as
 * `color(srgb 0.9937 0.7365 0.3914)`, which no test can compare with monday's measured
 * `rgb(253, 188, 100)`. A bucket colour that is not a hex — a CSS variable, which is what the
 * in-memory source hands the harness — keeps `color-mix`, since only the browser can resolve it.
 */
export function laneHeaderColor(bucketColor) {
    const hex = /#([0-9a-f]{3}|[0-9a-f]{6})\b/i.exec(bucketColor ?? '#c4c4c4');
    if (hex === null)
        return `color-mix(in srgb, ${bucketColor ?? '#c4c4c4'} 80%, #ffffff)`;
    const digits = hex[1];
    const full = digits.length === 3 ? digits.replace(/./g, (char) => char + char) : digits;
    const channel = (offset) => {
        const base = Number.parseInt(full.slice(offset, offset + 2), 16);
        return Math.round(base + (255 - base) * LANE_HEADER_LIGHTEN);
    };
    return `rgb(${String(channel(0))}, ${String(channel(2))}, ${String(channel(4))})`;
}
export const kanbanView = defineViewType({
    id: 'kanban',
    title: 'Kanban',
    mondayViewType: 'KanbanBoardView',
    kind: 'APP',
    settingsSchema: kanbanViewSettingsSchema,
    defaultSettings: DEFAULT_KANBAN_VIEW_SETTINGS,
    normaliseSettings: (input) => normaliseOverDefaults('kanban', kanbanViewSettingsSchema, DEFAULT_KANBAN_VIEW_SETTINGS, mondayShapedSettings(input, DEFAULT_KANBAN_VIEW_SETTINGS, kanbanSettingsFromMonday)),
    requiredColumnTypes: [],
    supportsFilters: true,
    supportsSort: true,
    supportsGroupBy: true,
    canBeDefault: true,
    implemented: true,
    plannedIn: null,
    gallery: { order: 5, section: 'board-views' },
});
//# sourceMappingURL=index.js.map